/**
 * Client Workflow Service
 *
 * Handles business logic and workflows for clients.
 * This service manages validations, dependency checking, and other business operations.
 */

import { RepositoryResult } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Client } from '@/shared/types';

/**
 * Client dependency information
 */
export type ClientDependencies = {
  hasDependencies: boolean;
  jobsCount: number;
  quotesCount: number;
  invoicesCount: number;
};

/**
 * Client Workflow Service
 * Provides business logic and workflow operations for clients
 */
export class ClientWorkflowService {
  private tableName = 'clients' as const;

  /**
   * Validate client data before creation/update
   */
  validateClientData(clientData: Partial<Client>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!clientData.name || clientData.name.trim() === '') {
      errors.push('Client name is required');
    }

    if (!clientData.email || clientData.email.trim() === '') {
      errors.push('Email is required');
    } else {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(clientData.email)) {
        errors.push('Invalid email format');
      }
    }

    if (!clientData.phone || clientData.phone.trim() === '') {
      errors.push('Phone number is required');
    }

    // Validate address if provided
    if (clientData.address && clientData.address.trim().length > 500) {
      errors.push('Address must be less than 500 characters');
    }

    // Validate company if provided
    if (clientData.company && clientData.company.trim().length > 200) {
      errors.push('Company name must be less than 200 characters');
    }

    // Validate tax ID if provided
    if (clientData.taxId && clientData.taxId.trim().length > 50) {
      errors.push('Tax ID must be less than 50 characters');
    }

    // Validate notes if provided
    if (clientData.notes && clientData.notes.trim().length > 2000) {
      errors.push('Notes must be less than 2000 characters');
    }

    // Validate logo URL if provided
    if (clientData.logoUrl) {
      try {
        new URL(clientData.logoUrl);
      } catch {
        errors.push('Logo URL must be a valid URL');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Check if a client has dependencies (jobs, quotes, invoices)
   * Useful for preventing deletion of clients that are referenced elsewhere
   */
  async hasDependencies(id: string): Promise<RepositoryResult<ClientDependencies>> {
    try {
      const db = getDbClient();

      // Check for jobs
      const { count: jobsCount } = await db
        .from('jobs')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', id)
        .is('is_deleted', false);

      // Check for quotes
      const { count: quotesCount } = await db
        .from('quotes')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', id);

      // Check for invoices
      const { count: invoicesCount } = await db
        .from('invoices')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', id);

      const hasDependencies = (jobsCount || 0) > 0 || (quotesCount || 0) > 0 || (invoicesCount || 0) > 0;

      return {
        success: true,
        data: {
          hasDependencies,
          jobsCount: jobsCount || 0,
          quotesCount: quotesCount || 0,
          invoicesCount: invoicesCount || 0,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'checking client dependencies'),
      };
    }
  }

  /**
   * Format client name for display
   */
  formatDisplayName(client: Client): string {
    if (client.company) {
      return `${client.name} (${client.company})`;
    }
    return client.name;
  }

  /**
   * Generate client initials for avatar
   */
  generateInitials(client: Client): string {
    const names = client.name.trim().split(' ');
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  }

  /**
   * Check if email is unique for a user
   */
  async isEmailUnique(userId: string, email: string, excludeId?: string): Promise<RepositoryResult<boolean>> {
    try {
      const db = getDbClient();

      let query = db
        .from(this.tableName)
        .select('id')
        .eq('user_id', userId)
        .eq('email', email)
        .is('is_deleted', false);

      if (excludeId) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query.maybeSingle();

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: !data, // Returns true if email is unique (no existing client found)
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'checking email uniqueness'),
      };
    }
  }

  /**
   * Get client statistics
   */
  async getClientStats(clientId: string): Promise<RepositoryResult<{
    totalRevenue: number;
    jobsCount: number;
    quotesCount: number;
    invoicesCount: number;
    lastActivity: string | null;
  }>> {
    try {
      const db = getDbClient();

      // Get jobs count
      const { count: jobsCount } = await db
        .from('jobs')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', clientId)
        .is('is_deleted', false);

      // Get quotes count
      const { count: quotesCount } = await db
        .from('quotes')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', clientId);

      // Get invoices and total revenue
      const { data: invoices } = await db
        .from('invoices')
        .select('total_amount, updated_at')
        .eq('client_id', clientId);

      const invoicesCount = invoices?.length || 0;
      const totalRevenue = invoices?.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) || 0;

      // Get last activity (most recent update across all tables)
      const lastActivity = await this.getLastActivityDate(clientId);

      return {
        success: true,
        data: {
          totalRevenue,
          jobsCount: jobsCount || 0,
          quotesCount: quotesCount || 0,
          invoicesCount,
          lastActivity,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching client statistics'),
      };
    }
  }

  /**
   * Get last activity date for a client
   */
  private async getLastActivityDate(clientId: string): Promise<string | null> {
    try {
      const db = getDbClient();

      // Get most recent job date
      const { data: lastJob } = await db
        .from('jobs')
        .select('updated_at')
        .eq('client_id', clientId)
        .is('is_deleted', false)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Get most recent quote date
      const { data: lastQuote } = await db
        .from('quotes')
        .select('updated_at')
        .eq('client_id', clientId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Get most recent invoice date
      const { data: lastInvoice } = await db
        .from('invoices')
        .select('updated_at')
        .eq('client_id', clientId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const dates = [
        lastJob?.updated_at,
        lastQuote?.updated_at,
        lastInvoice?.updated_at,
      ].filter(Boolean) as string[];

      if (dates.length === 0) {
        return null;
      }

      // Return the most recent date
      return dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
    } catch {
      return null;
    }
  }

  /**
   * Categorize client by activity level
   */
  async categorizeClient(clientId: string): Promise<RepositoryResult<'active' | 'inactive' | 'new'>> {
    try {
      const statsResult = await this.getClientStats(clientId);

      if (!statsResult.success || !statsResult.data) {
        return {
          success: false,
          error: 'Unable to categorize client',
        };
      }

      const stats = statsResult.data;

      // New client: no activity yet
      if (!stats.lastActivity) {
        return {
          success: true,
          data: 'new',
        };
      }

      // Active: activity in last 30 days
      const lastActivityDate = new Date(stats.lastActivity);
      const daysSinceActivity = Math.floor((Date.now() - lastActivityDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysSinceActivity <= 30) {
        return {
          success: true,
          data: 'active',
        };
      }

      // Inactive: no activity in over 30 days
      return {
        success: true,
        data: 'inactive',
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'categorizing client'),
      };
    }
  }

  /**
   * Sanitize client notes (remove HTML, normalize whitespace)
   */
  sanitizeNotes(notes: string): string {
    if (!notes) return '';

    // Remove HTML tags
    let sanitized = notes.replace(/<[^>]*>/g, '');

    // Normalize whitespace
    sanitized = sanitized.replace(/\s+/g, ' ').trim();

    return sanitized;
  }

  /**
   * Helper method to handle errors consistently
   */
  private handleError(error: any, context: string): string {
    console.error(`Error ${context}:`, error);
    return error?.message || `Failed to ${context}`;
  }
}

// Export singleton instance
export const clientWorkflowService = new ClientWorkflowService();
